import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform, useVelocity, type AnimationPlaybackControls, type Transition } from "motion/react";
import { cn } from "@repo/blacksmith-ui/cn";

/** Counters snap instead of rolling when this changes, e.g. a session switch that keeps them mounted. */
export const RollingNumberScopeContext = createContext<string | undefined>(undefined);
const ROLL_TRANSITION = {
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1]
} satisfies Transition;
const REVEAL_TRANSITION = {
  duration: 0.35,
  ease: [0.33, 1, 0.68, 1]
} satisfies Transition;
/** Outlasts both transitions, so the counter settles back to plain text once still. */
const SETTLE_MS = 500;

/** Three copies of 0-9: a digit rests in the middle one and rolls up or down into a neighbour without wrapping. */
const REEL = Array.from({
  length: 30
}, (_, i) => i % 10);
const REST_OFFSET = 10;
const BLUR_PX_PER_DIGIT_PER_SECOND = 0.25;
const MAX_BLUR_PX = 2;
const SHOWN = {
  width: "auto",
  opacity: 1,
  filter: "blur(0px)"
};
const HIDDEN = {
  width: 0,
  opacity: 0,
  filter: "blur(4px)"
};

/** Glyphs come from `data-text` pseudo-content so the reel stays out of textContent and copied text. */
const GLYPH_CLASS = "whitespace-pre before:content-[attr(data-text)]";
interface Roll {
  from: number;
  to: number;
  direction: 1 | -1;
  /** Digit columns rendered for this roll, so a column that leaves can animate away. */
  places: number;
}

/** Null while settled; a roll from the first change after mount until the value has been still for SETTLE_MS. */
function useRoll(value) {
  const $ = _c(6);
  const reduceMotion = useReducedMotion();
  const scope = useContext(RollingNumberScopeContext);
  let t0;
  if ($[0] !== scope || $[1] !== value) {
    t0 = {
      value,
      scope,
      roll: null
    };
    $[0] = scope;
    $[1] = value;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [state, setState] = useState(t0);
  if (state.value !== value || state.scope !== scope) {
    const previous = state.roll;
    setState({
      value,
      scope,
      roll: reduceMotion || state.scope !== scope ? null : {
        from: previous?.from ?? state.value,
        to: value,
        direction: value > state.value ? 1 : -1,
        places: Math.max(previous?.places ?? String(state.value).length, String(value).length)
      }
    });
  }
  const {
    roll
  } = state;
  let t1;
  let t2;
  if ($[3] !== roll) {
    t1 = () => {
      if (!roll) {
        return;
      }
      const timer = setTimeout(() => setState(current => current.roll === roll ? {
        ...current,
        roll: null
      } : current), SETTLE_MS);
      return () => clearTimeout(timer);
    };
    t2 = [roll];
    $[3] = roll;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return roll;
}

/**
 * A count whose digits roll with motion blur when it changes, e.g. 9 → 10
 * rolls the ones up to 0 while a blurred 1 slides in. Renders plain text at
 * rest, so mounts, copies and queries see the bare number.
 */
export function RollingNumber(t0) {
  const $ = _c(9);
  const {
    value
  } = t0;
  const roll = useRoll(value);
  if (!roll) {
    let t1;
    if ($[0] !== value) {
      t1 = <>{value}</>;
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== value) {
    t1 = <span className="sr-only">{value}</span>;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== roll) {
    t2 = <span aria-hidden={true} className="whitespace-nowrap"><RollingDigits roll={roll} /></span>;
    $[4] = roll;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <span>{t1}{t2}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/**
 * A {@link RollingNumber} with its noun, whose plural ending blurs in and out
 * as the count crosses one: `1 subagent` → `2 subagents`.
 */
export function RollingCount(t0) {
  const $ = _c(36);
  const {
    count,
    one,
    other
  } = t0;
  const roll = useRoll(count);
  const label = `${count} ${count === 1 ? one : other}`;
  if (!roll) {
    let t1;
    if ($[0] !== label) {
      t1 = <>{label}</>;
      $[0] = label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== count || $[3] !== label || $[4] !== one || $[5] !== other || $[6] !== roll) {
    const stem = commonPrefix(one, other);
    if ($[15] !== label) {
      t7 = <span className="sr-only">{label}</span>;
      $[15] = label;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t2 = true;
    t3 = "whitespace-nowrap";
    if ($[17] !== roll) {
      t4 = <RollingDigits roll={roll} />;
      $[17] = roll;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    const t8 = ` ${stem}`;
    if ($[19] !== t8) {
      t5 = <span data-text={t8} className={GLYPH_CLASS} />;
      $[19] = t8;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    t6 = <NounEnding text={one.slice(stem.length)} shown={count === 1} shownAtStart={roll.from === 1} />;
    T0 = NounEnding;
    t1 = other.slice(stem.length);
    $[2] = count;
    $[3] = label;
    $[4] = one;
    $[5] = other;
    $[6] = roll;
    $[7] = T0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    T0 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
  }
  const t8 = count !== 1;
  const t9 = roll.from !== 1;
  let t10;
  if ($[21] !== T0 || $[22] !== t1 || $[23] !== t8 || $[24] !== t9) {
    t10 = <T0 text={t1} shown={t8} shownAtStart={t9} />;
    $[21] = T0;
    $[22] = t1;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t2 || $[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t11 = <span aria-hidden={t2} className={t3}>{t4}{t5}{t6}{t10}</span>;
    $[26] = t10;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t7) {
    t12 = <span>{t7}{t11}</span>;
    $[33] = t11;
    $[34] = t7;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}
function RollingDigits(t0) {
  const $ = _c(5);
  const {
    roll
  } = t0;
  const from = String(roll.from);
  const to = String(roll.to);
  let t1;
  if ($[0] !== from || $[1] !== roll.direction || $[2] !== roll.places || $[3] !== to) {
    t1 = <>{Array.from({
        length: roll.places
      }, (_, index) => {
        const place = roll.places - 1 - index;
        return <DigitColumn key={place} digit={digitAt(to, place)} fromDigit={digitAt(from, place)} direction={roll.direction} />;
      })}</>;
    $[0] = from;
    $[1] = roll.direction;
    $[2] = roll.places;
    $[3] = to;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/** `digitAt("120", 0)` → 0, `digitAt("120", 2)` → 1, `digitAt("120", 3)` → undefined. */
function digitAt(digits: string, place: number): number | undefined {
  const char = digits[digits.length - 1 - place];
  return char === undefined ? undefined : Number(char);
}
function DigitColumn(t0) {
  const $ = _c(27);
  const {
    digit,
    fromDigit,
    direction
  } = t0;
  const [shownDigit, setShownDigit] = useState(digit ?? fromDigit ?? 0);
  if (digit !== undefined && digit !== shownDigit) {
    setShownDigit(digit);
  }
  const y = useMotionValue((fromDigit ?? shownDigit) + REST_OFFSET);
  let t1;
  if ($[0] !== y) {
    t1 = y.get();
    $[0] = y;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const target = useRef(t1);
  const controls = useRef(null);
  let t2;
  let t3;
  if ($[2] !== direction || $[3] !== shownDigit || $[4] !== y) {
    t2 = () => {
      const next = shownDigit + REST_OFFSET;
      if (next === target.current) {
        return;
      }
      target.current = next;
      controls.current?.stop();
      const current = y.get();
      y.jump(direction === 1 ? next - modulo(next - current, 10) : next + modulo(current - next, 10));
      controls.current = animate(y, next, ROLL_TRANSITION);
    };
    t3 = [shownDigit, direction, y];
    $[2] = direction;
    $[3] = shownDigit;
    $[4] = y;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== y) {
    t4 = () => () => {
      controls.current?.stop();
      target.current = y.get();
    };
    t5 = [y];
    $[7] = y;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  const translateY = useTransform(y, _temp);
  const velocity = useVelocity(y);
  const filter = useTransform(velocity, _temp2);
  const t6 = fromDigit === undefined || digit === undefined ? "overflow-hidden" : "overflow-x-visible overflow-y-clip";
  let t7;
  if ($[10] !== t6) {
    t7 = cn("relative inline-flex justify-center", t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = fromDigit === undefined ? HIDDEN : SHOWN;
  const t9 = digit === undefined ? HIDDEN : SHOWN;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("invisible", GLYPH_CLASS);
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== shownDigit) {
    t11 = <span data-text={shownDigit} className={t10} />;
    $[13] = shownDigit;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== filter || $[16] !== translateY) {
    t12 = {
      y: translateY,
      filter
    };
    $[15] = filter;
    $[16] = translateY;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = REEL.map(_temp3);
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12) {
    t14 = <motion.span className="absolute inset-x-0 top-0 flex flex-col items-center" style={t12}>{t13}</motion.span>;
    $[19] = t12;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t11 || $[22] !== t14 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t15 = <motion.span className={t7} initial={t8} animate={t9} transition={REVEAL_TRANSITION}>{t11}{t14}</motion.span>;
    $[21] = t11;
    $[22] = t14;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}
function _temp3(reelDigit, index) {
  return <span key={index} data-text={reelDigit} className={GLYPH_CLASS} />;
}
function _temp2(v_0) {
  return `blur(${Math.min(Math.abs(v_0) * BLUR_PX_PER_DIGIT_PER_SECOND, MAX_BLUR_PX)}px)`;
}
function _temp(v) {
  return `${-v / REEL.length * 100}%`;
}
function NounEnding(t0) {
  const $ = _c(6);
  const {
    text,
    shown,
    shownAtStart
  } = t0;
  if (!text) {
    return null;
  }
  const t1 = shownAtStart ? SHOWN : HIDDEN;
  const t2 = shown ? SHOWN : HIDDEN;
  let t3;
  if ($[0] !== text) {
    t3 = <span data-text={text} className={GLYPH_CLASS} />;
    $[0] = text;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = <motion.span className="inline-flex overflow-hidden" initial={t1} animate={t2} transition={REVEAL_TRANSITION}>{t3}</motion.span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function commonPrefix(a: string, b: string): string {
  let length = 0;
  while (length < a.length && a[length] === b[length]) length++;
  return a.slice(0, length);
}
function modulo(n: number, divisor: number): number {
  return (n % divisor + divisor) % divisor;
}